import { useCallback, useEffect, useState } from "react";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { UPDATE_RESTAURANT } from "src/constants/mutations";
import { GET_STORE_RECEIPT_SETTINGS } from "src/constants/queries";
import { useAuth } from "src/contexts/auth-provider";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { BtnLoader } from "src/Loader";
import FormRowVertical from "src/components/common/FormRowVertical";
import { failureToast, successToast } from "src/utils/toasts";

const DEFAULT_POPULARITY_SORTING_DAYS = 90;

const OrderingSettings = () => {
  const { axBe } = useAxiosInterceptor();
  const { hasRestaurant } = useAuth();
  const [loading, setLoading] = useState(false);
  const [popularItemSortingEnabled, setPopularItemSortingEnabled] = useState<boolean>(false);
  const [popularItemSortingDays, setPopularItemSortingDays] = useState<number>(DEFAULT_POPULARITY_SORTING_DAYS);
  const [initialPopularItemSortingEnabled, setInitialPopularItemSortingEnabled] = useState<boolean>(false);
  const [initialPopularItemSortingDays, setInitialPopularItemSortingDays] = useState<number>(DEFAULT_POPULARITY_SORTING_DAYS);

  const fetchSettings = useCallback(async () => {
    if (!hasRestaurant?.correlationId) return;
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: GET_STORE_RECEIPT_SETTINGS,
        variables: { retrieveStoresInput: { storeCorrelationIds: [hasRestaurant.correlationId] } },
      });
      const stores = data?.data?.stores || [];
      const s = stores[0] || null;
      const storePopularEnabled = Boolean(s?.popularItemSortingEnabled);
      const storePopularDays = Number.isFinite(Number(s?.popularItemSortingDays)) && Number(s?.popularItemSortingDays) > 0
        ? Number(s.popularItemSortingDays)
        : DEFAULT_POPULARITY_SORTING_DAYS;

      setPopularItemSortingEnabled(storePopularEnabled);
      setPopularItemSortingDays(storePopularDays);
      setInitialPopularItemSortingEnabled(storePopularEnabled);
      setInitialPopularItemSortingDays(storePopularDays);
    } catch (e) {
      console.error("Error fetching ordering settings:", e);
    }
  }, [axBe, hasRestaurant?.correlationId]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const hasChanged = () =>
    initialPopularItemSortingEnabled !== popularItemSortingEnabled ||
    initialPopularItemSortingDays !== popularItemSortingDays;

  const onSave = async () => {
    if (!hasRestaurant?.correlationId) return;
    const normalizedPopularDays = Math.floor(popularItemSortingDays);

    if (popularItemSortingEnabled && (!Number.isFinite(normalizedPopularDays) || normalizedPopularDays < 1)) {
      failureToast("Popular item sorting days must be greater than 0.");
      return;
    }

    setLoading(true);
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: UPDATE_RESTAURANT,
        variables: {
          updateStoreInput: {
            storeCorrelationId: hasRestaurant.correlationId,
            popularItemSortingEnabled,
            popularItemSortingDays: popularItemSortingEnabled
              ? normalizedPopularDays
              : Math.max(1, normalizedPopularDays || DEFAULT_POPULARITY_SORTING_DAYS),
          },
        },
      });
      if (data?.errors?.length) throw data.errors;
      setInitialPopularItemSortingEnabled(popularItemSortingEnabled);
      setInitialPopularItemSortingDays(normalizedPopularDays);
      setPopularItemSortingDays(normalizedPopularDays);
      await fetchSettings();
      successToast("Ordering settings updated!");
    } catch (error) {
      console.error("Error updating ordering settings:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-[760px] space-y-4">
      <div className="bg-[#ffffff] rounded-[16px] px-6 py-5 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.04)]">
        <h2 className="text-[18px] font-semibold text-[#111827] tracking-[-0.02em] mb-1">Popular item sorting</h2>
        <p className="text-[14px] text-[#6b7280] mb-4">
          When enabled, menu items inside each category are ordered by how often they were sold in the selected lookback window.
        </p>

        <div className="flex flex-col gap-4">
          <label className="flex items-center gap-2 rounded-xl border border-[#E5E7EB] bg-white px-4 py-3 cursor-pointer">
            <input
              type="checkbox"
              checked={popularItemSortingEnabled}
              onChange={(event) => setPopularItemSortingEnabled(event.target.checked)}
              className="receipt-lang-check w-6 h-6 min-w-[24px] min-h-[24px] shrink-0 rounded border border-[#9B9B9B] cursor-pointer appearance-none"
            />
            <span className="text-sm text-[#111827]">
              Enable popularity-based sorting for order-taking categories
            </span>
          </label>

          <FormRowVertical label="Lookback days" className="mt-0">
            <input
              type="number"
              min={1}
              step={1}
              value={popularItemSortingDays}
              disabled={!popularItemSortingEnabled}
              onChange={(event) => setPopularItemSortingDays(Number(event.target.value))}
              className={`w-full h-11 rounded-xl border px-3 text-sm text-[#111827] outline-none transition-colors ${popularItemSortingEnabled ? "border-[#D1D5DB] bg-white focus:border-[#FD7657]" : "border-[#E5E7EB] bg-[#F9FAFB] text-[#9CA3AF]"}`}
            />
          </FormRowVertical>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={onSave}
              disabled={!hasChanged() || loading}
              className="inline-flex items-center justify-center rounded-xl bg-[#FD7657] px-5 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? <BtnLoader /> : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderingSettings;
